/**
 * GoalOverviewForm - Reusable form for Goal overview fields
 * 
 * Used in:
 * - GoalDetailModal (edit mode)
 * - GoalMergeModal (source view + merged form)
 * 
 * Props:
 * - formData: { title, description, status, owner_id, target_date }
 * - onChange: (field, value) => void
 * - isEditing: boolean (controls whether fields are editable)
 * - isDisabled: boolean (overrides isEditing for locked states like Active goals)
 * - colors: theme colors from useSafeMode
 * - teamMembers: array of team members for owner dropdown
 * - computedMetrics: optional computed data for investment badge
 * - paceData: optional pace data for KPI status banner
 * - showStatusBanner: boolean (show/hide pace status banner)
 * - mode: 'edit' | 'create' | 'view' (defaults to 'edit')
 * - errors: { [field]: string } - validation errors to display
 */

import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import ReactMarkdown from 'react-markdown';
import { MarkdownEditor } from '../ui/markdown-editor';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DatePickerWithMarkers } from '../ui/date-picker-with-markers';
import { cn } from '../../lib/utils';
import { User, Calendar, TrendingUp, TrendingDown, AlertTriangle, AlertCircle } from 'lucide-react';

// Status options for goals
export const GOAL_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-500' },
  { value: 'active', label: 'Active', color: 'bg-blue-500' },
  { value: 'achieved', label: 'Achieved', color: 'bg-green-500' },
  { value: 'abandoned', label: 'Abandoned', color: 'bg-red-500' },
];

// Required fields for goal
export const GOAL_REQUIRED_FIELDS = ['title', 'target_date'];

const GoalOverviewForm = ({
  formData,
  onChange,
  isEditing = true,
  isDisabled = false,
  colors,
  teamMembers = [],
  computedMetrics = null,
  paceData = null,
  showStatusBanner = true,
  mode = 'edit',
  errors = {},
}) => {
  const handleChange = (field, value) => {
    if (onChange) {
      onChange(field, value);
    }
  };

  // Helper to get error styling for a field
  const getFieldErrorClass = (field) => {
    return errors[field] ? 'border-red-500 ring-1 ring-red-500' : '';
  };

  // Investment badge display
  const getInvestmentBadge = () => {
    const badge = computedMetrics?.investment_badge || 'low';
    const pct = computedMetrics?.investment_pct || 0;
    
    const badges = {
      high: { icon: '🔥', label: 'High Bet', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
      medium: { icon: '💡', label: 'Medium', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
      low: { icon: '🌱', label: 'Low', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' }
    };
    
    const config = badges[badge] || badges.low;
    return (
      <span className={cn("px-2 py-1 rounded-full text-xs font-medium", config.color)}>
        {config.icon} {config.label} ({pct}%)
      </span>
    );
  };

  const effectiveDisabled = isDisabled || !isEditing;
  const kpisCount = formData?.kpis?.length || 0;

  return (
    <div className="space-y-4">
      {/* KPI Status Summary Banner - Only show for Active goals with KPIs */}
      {showStatusBanner && formData.status === 'active' && paceData && kpisCount > 0 && (
        <div className={cn(
          "p-4 rounded-lg border",
          paceData.pace_status === 'off_track' 
            ? "bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700"
            : paceData.pace_status === 'insufficient_data' || paceData.pace_status === 'no_data'
              ? "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800"
              : "bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700"
        )}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center",
                paceData.pace_status === 'off_track'
                  ? "bg-red-100 dark:bg-red-900/40"
                  : paceData.pace_status === 'insufficient_data' || paceData.pace_status === 'no_data'
                    ? "bg-amber-100 dark:bg-amber-900/40"
                    : "bg-green-100 dark:bg-green-900/40"
              )}>
                {paceData.pace_status === 'on_track' ? (
                  <TrendingUp className="w-5 h-5 text-green-600" />
                ) : paceData.pace_status === 'off_track' ? (
                  <TrendingDown className="w-5 h-5 text-red-600" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                )}
              </div>
              <div>
                <p className={cn(
                  "font-semibold",
                  paceData.pace_status === 'off_track' 
                    ? "text-red-800 dark:text-red-200" 
                    : paceData.pace_status === 'on_track'
                      ? "text-green-800 dark:text-green-200"
                      : "text-amber-800 dark:text-amber-200"
                )}>
                  {paceData.aggregate?.method_label}: {paceData.pace_status_label}
                  {paceData.days_remaining !== null && (
                    <span className="font-normal ml-2">• {paceData.days_remaining} days remaining</span>
                  )}
                </p>
                <p className={cn(
                  "text-sm mt-0.5",
                  paceData.pace_status === 'off_track'
                    ? "text-red-700 dark:text-red-300"
                    : paceData.pace_status === 'on_track'
                      ? "text-green-700 dark:text-green-300"
                      : "text-amber-700 dark:text-amber-300"
                )}>
                  {paceData.aggregate?.summary || 'No KPI status data'}
                </p>
              </div>
            </div>
            {/* KPI Breakdown Pills */}
            {paceData.aggregate?.breakdown && (
              <div className="flex items-center gap-2">
                {paceData.aggregate.breakdown.on_track > 0 && (
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400">
                    {paceData.aggregate.breakdown.on_track} On Track
                  </span>
                )}
                {paceData.aggregate.breakdown.off_track > 0 && (
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400">
                    {paceData.aggregate.breakdown.off_track} Off Track
                  </span>
                )}
                {paceData.aggregate.breakdown.insufficient_data > 0 && (
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400">
                    {paceData.aggregate.breakdown.insufficient_data} Insufficient
                  </span>
                )}
                {paceData.aggregate.breakdown.no_data > 0 && (
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                    {paceData.aggregate.breakdown.no_data} No Data
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Key Attributes Section */}
      <div className="space-y-4">
        {/* Title Row */}
        <div>
          <Label className={cn(colors.textSecondary, errors.title && "text-red-500")}>
            Title <span className="text-red-500">*</span>
          </Label>
          {isEditing && !isDisabled ? (
            <>
              <Input
                value={formData.title || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="e.g., Crush Churn in Q1"
                disabled={effectiveDisabled}
                className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary, getFieldErrorClass('title'))}
                data-testid="goal-title-input"
              />
              {errors.title && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.title}
                </p>
              )}
            </>
          ) : (
            <p className={cn("mt-1 p-2 rounded", colors.bgTertiary, colors.textPrimary)}>
              {formData.title || 'Not specified'}
            </p>
          )}
        </div>
        
        {/* Status & Owner & Target Date Row - side by side */}
        <div className="grid grid-cols-3 gap-4 items-end">
          <div className="flex flex-col">
            <Label className={colors.textSecondary}>Status</Label>
            {isEditing ? (
              <Select value={formData.status || 'draft'} onValueChange={(v) => handleChange('status', v)}>
                <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary)}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                  {GOAL_STATUS_OPTIONS.map(s => (
                    <SelectItem key={s.value} value={s.value}>
                      <div className="flex items-center gap-2">
                        <span className={cn("w-2 h-2 rounded-full", s.color)} />
                        {s.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="mt-1 flex items-center gap-2">
                <span className={cn(
                  "w-2 h-2 rounded-full",
                  GOAL_STATUS_OPTIONS.find(s => s.value === formData.status)?.color || 'bg-gray-500'
                )} />
                <span className={colors.textPrimary}>
                  {GOAL_STATUS_OPTIONS.find(s => s.value === formData.status)?.label || 'Draft'}
                </span>
              </div>
            )}
          </div>
          
          <div className="flex flex-col">
            <Label className={cn("flex items-center gap-1", colors.textSecondary)}>
              <User className="w-3 h-3" /> Owner
            </Label>
            {isEditing && !isDisabled ? (
              <Select 
                value={formData.owner_id || 'none'} 
                onValueChange={(v) => handleChange('owner_id', v === 'none' ? '' : v)}
                disabled={effectiveDisabled}
              >
                <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary)}>
                  <SelectValue placeholder="Select owner..." />
                </SelectTrigger>
                <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {teamMembers.map(m => (
                    <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className={cn("mt-1", colors.textPrimary)}>
                {teamMembers.find(m => m.user_id === formData.owner_id)?.name || 'Unassigned'}
              </p>
            )}
          </div>
          
          <div className="flex flex-col">
            <Label className={cn("flex items-center gap-1", colors.textSecondary, errors.target_date && "text-red-500")}>
              <Calendar className="w-3 h-3" /> Target Date <span className="text-red-500">*</span>
            </Label>
            {isEditing && !isDisabled ? (
              <div className="mt-1">
                <DatePickerWithMarkers
                  value={formData.target_date}
                  onChange={(date) => handleChange('target_date', date)}
                  parentDeadlines={[]}
                  placeholder="Select target date"
                  disabled={effectiveDisabled}
                  className={getFieldErrorClass('target_date')}
                />
                {errors.target_date && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.target_date}
                  </p>
                )}
              </div>
            ) : (
              <p className={cn("mt-1", colors.textPrimary)}>
                {formData.target_date 
                  ? new Date(formData.target_date).toLocaleDateString()
                  : 'Not set'}
              </p>
            )}
          </div>
        </div>
        
        {/* Investment Badge - Read only, only show in edit mode with computed metrics */}
        {mode === 'edit' && computedMetrics && (
          <div className="flex items-center gap-4">
            <span className={cn("text-sm", colors.textSecondary)}>Investment:</span>
            {getInvestmentBadge()}
          </div>
        )}
      </div>

      {/* Description */}
      <div>
        <Label className={colors.textSecondary}>Description</Label>
        {isEditing && !isDisabled ? (
          <div className="mt-1" data-testid="goal-description-input">
            <MarkdownEditor
              content={formData.description || ''}
              onChange={(value) => handleChange('description', value)}
              placeholder="Describe the strategic objective..."
              minHeight={250}
              disabled={effectiveDisabled}
            />
          </div>
        ) : (
          <div className={cn("mt-1 p-3 rounded-lg", colors.bgTertiary)}>
            {formData.description ? (
              <div className={cn("prose prose-sm max-w-none dark:prose-invert", colors.textPrimary)}>
                <ReactMarkdown>{formData.description}</ReactMarkdown>
              </div>
            ) : (
              <p className={cn("text-sm", colors.textMuted)}>No description provided</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GoalOverviewForm;
